import { ChangeDetectionStrategy, Component, input } from '@angular/core';

import { CiCalculationResult, CiRecord } from '../../../_services/ci.service';
import { benchmarkSourceDefinition, categoryDefinition, isLandLed } from '../config/ci-options.config';
import { CiFormValue, PropertyCategory } from '../models/ci-form.model';
import {
  formatCompactCurrency,
  formatPct,
  formatPsf,
  formatSignedPct,
} from '../../../core/utils/format.util';

/** Every figure below is read straight off the backend calculation result.
 *  The API returns ratios (0.05 = 5%), so percentages are scaled ×100 for display only. */
@Component({
  selector: 'ci-step-review',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './step-review.component.html',
})
export class StepReviewComponent {
  readonly value = input.required<CiFormValue>();
  readonly result = input<CiCalculationResult | CiRecord | null>(null);
  readonly category = input.required<PropertyCategory>();
  readonly calculating = input(false);
  readonly calculationError = input<string | null>(null);
  /** What still blocks a complete calculation — see calculationIssues(). */
  readonly issues = input<string[]>([]);

  psf = formatPsf;
  compact = formatCompactCurrency;
  signedPct = formatSignedPct;
  pct = formatPct;

  /** Set once the record has been persisted as a draft or generated CI. */
  status(): string | null {
    const r = this.result() as Partial<CiRecord> | null;
    return r?.status ?? null;
  }

  summaryRows(): { label: string; value: string }[] {
    const v = this.value();
    const p = v.property;
    const r = this.result();
    const dash = '—';
    const landLed = isLandLed(v.category);

    const rows = [
      { label: 'Property', value: p.propertyName || dash },
      { label: 'Property Type', value: categoryDefinition(v.category).label },
      { label: 'CI Level', value: categoryDefinition(v.category).basis },
      {
        label: landLed ? 'Current Asking Price' : 'Quoted Price',
        value: formatCompactCurrency(p.quotedPrice),
      },
    ];

    if (!r) {
      rows.push({ label: 'Quoted PSF', value: dash });
      return rows;
    }

    rows.push({ label: 'Quoted PSF', value: this.psf(r.quotedPsf) });

    if (landLed) {
      return rows.concat(this.landRows(r));
    } else {
      rows.push(
        { label: 'Benchmark Source', value: benchmarkSourceDefinition(v.benchmarking.source).label },
        { label: 'Benchmark PSF', value: this.psf(r.benchmarkPsf) },
        { label: 'Average Transaction PSF', value: this.psf(r.last5TransactionsAvgPsf) },
        { label: 'Last Transaction PSF', value: this.psf(r.lastTransactionPsf) },
        {
          label: 'Transaction Age',
          value: r.lastTransactionAgeMonths != null ? `${r.lastTransactionAgeMonths} months` : dash,
        },
        { label: 'UDS Support', value: r.udsSupportPercent != null ? this.pct(r.udsSupportPercent * 100, 1) : dash },
        { label: 'Rental Yield', value: r.rentalYield != null ? this.pct(r.rentalYield * 100, 1) : dash },
      );
    }

    const w = r.checklistWeightages;
    const weightage = (n: number | null | undefined) => (n != null ? this.signedPct(n * 100) : dash);
    rows.push(
      { label: 'Locality Weightage', value: weightage(w?.locality) },
      { label: 'Builder Tier Weightage', value: weightage(w?.builderTier) },
      { label: 'Legal Issue Weightage', value: weightage(w?.legalIssue) },
      { label: 'Scarcity Weightage', value: weightage(w?.scarcity) },
      { label: 'Same Property Overpricing Weightage', value: weightage(w?.samePropertyOverpricing) },
      { label: 'Checklist Weightage', value: weightage(r.totalChecklistWeightagePercent) },
      { label: 'Revised Benchmark', value: this.psf(r.revisedBenchmark) },
      { label: 'Ask vs Benchmark', value: this.signedPct(r.askVsBenchmarkPercent * 100) },
      { label: 'Initial Verdict', value: r.initialVerdict || dash },
      { label: 'Adjusted Verdict', value: r.adjustedVerdict || dash },
      { label: 'Final Verdict', value: r.circledIntelligenceVerdict || dash },
      { label: 'Suggested Offer', value: this.compact(r.suggestedOfferValue) },
      {
        label: 'Likely Close',
        value: `${this.compact(r.likelyCloseValueLow)} – ${this.compact(r.likelyCloseValueHigh)}`,
      },
      {
        label: 'Fair Value Range',
        value: `${this.compact(r.fairValueBandLow)} – ${this.compact(r.fairValueBandHigh)}`,
      },
    );

    return rows.concat(this.websiteRows(r));
  }

  /** Plot / house breakdown, in the order of the spec's calculation flow. */
  private landRows(r: CiCalculationResult | CiRecord): { label: string; value: string }[] {
    const dash = '—';
    const a = r.landAssessment;
    if (!a) {
      // Records calculated before the land engine have no breakdown.
      return [{ label: 'Base Rate', value: this.psf(r.benchmarkPsf) }];
    }
    const pct = (n: number) => this.signedPct(n * 100);
    const rows = [
      { label: 'Plot Area', value: `${a.plotArea} sq.ft` },
      { label: 'Circled Land Rate', value: this.psf(a.landRatePsf) },
      { label: 'Base Land Value', value: this.compact(a.baseLandValue) },
      { label: 'Orientation Adjustment', value: `${pct(a.orientationAdjustment)}${a.orientation ? ` (${a.orientation})` : ''}` },
      { label: 'Road Width Adjustment', value: `${pct(a.roadAdjustment)}${a.roadWidthFt ? ` (${a.roadWidthFt} ft)` : ''}` },
      { label: 'Main Road / Premium Plot', value: pct(a.premiumAdjustment) },
      { label: 'Total Land Adjustment', value: pct(a.totalLandAdjustment) },
      { label: 'Adjusted Land Value', value: this.compact(a.adjustedLandValue) },
    ];
    if (a.supplyOverrideApplied) {
      rows.push(
        { label: 'Supply Override', value: `Applied — ${this.compact(a.supplyOverrideValue ?? a.finalLandValue)} replaces ${this.compact(a.adjustedLandValue)}` },
        { label: 'Override Reason', value: a.supplyOverrideReason || '—' },
      );
    }
    rows.push({ label: 'Final Land Value', value: this.compact(a.finalLandValue) });
    if (r.propertyCategory === 'independent-house' || a.builtUpArea != null) {
      rows.push(
        { label: 'Built-up Area', value: a.builtUpArea != null ? `${a.builtUpArea} sq.ft` : dash },
        { label: 'Building Age', value: a.buildingAgeYears != null ? `${a.buildingAgeYears} years (${a.buildingAgeBand})` : dash },
        { label: 'Construction Classification', value: a.constructionClass ?? dash },
        { label: 'Building Value / sq ft', value: this.psf(a.buildingRatePsf) },
        { label: 'Total Building Value', value: this.compact(a.totalBuildingValue) },
        { label: 'Land vs Building', value: `${this.pct(a.landShare * 100, 0)} land · ${this.pct((1 - a.landShare) * 100, 0)} building` },
      );
    }
    rows.push(
      { label: 'Final CI Value', value: this.compact(a.assessedValue) },
      { label: 'Ask vs CI Value', value: this.signedPct(a.askVsCiPercent * 100, 2) },
      { label: 'Final Verdict', value: r.circledIntelligenceVerdict || dash },
      { label: 'Suggested Offer', value: this.compact(r.suggestedOfferValue) },
      {
        label: 'Likely Closing Range',
        value: `${this.compact(r.likelyCloseValueLow)} – ${this.compact(r.likelyCloseValueHigh)}`,
      },
    );
    return rows.concat(this.websiteRows(r));
  }

  private websiteRows(r: CiCalculationResult | CiRecord): { label: string; value: string }[] {
    const rows: { label: string; value: string }[] = [];
    if (r.websiteSuggestedOffer) {
      rows.push({ label: 'Website Suggested Offer', value: r.websiteSuggestedOffer });
    }
    if (r.websiteLikelyCloseBand) {
      rows.push({ label: 'Website Likely Close Band', value: r.websiteLikelyCloseBand });
    }
    return rows;
  }
}
